import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useDate,
  useOrderByFilter,
  useToQueryString,
} from '@/core'
import { TDailyReport, TLog } from '@/modules/projects/types'
import { useModuleQuery } from '../query/useModuleQuery'
import { useDailyReportQuery } from '../query/useDailyReportQuery'
import { useRoute, useRouter } from 'vue-router'
import { useLogsReportQuery } from '../query/useLogsReportQuery'
import { TRecordAction } from '@/core/types'

const [useProvideDailyReportModule, useDailyReportModule] =
  createInjectionState(() => {
    const route = useRoute()
    const router = useRouter()
    const rows = ref<number[]>([])
    const form = ref<TDailyReport>({})
    const logForm = ref<TLog>({})
    const { zonedTimeToUtc } = useDate()
    const coilStocksFilters = ref<ToQueryStringFilters>({
      search: '',
    })

    const dailyQueryParams = useToQueryString(toReactive(coilStocksFilters))

    const dailyQuery = useDailyReportQuery({
      modelId: computed(() => route.params?.id?.toString()),
      modelChildId: computed(() => route.params?.dailyId?.toString() ?? ''),
      enableQuery: {
        loadRecord: computed(() => !!route.params?.dailyId),
        loadRecords: computed(() => !!route.params?.id),
        loadCoilsProject: computed(() => !!route.params?.id),
        loadBundlesProject: computed(() => !!route.params?.id),
      },
      filters: {
        coilStocks: dailyQueryParams.queryParams,
      },
    })
    const logReportQuery = useLogsReportQuery({
      modelId: computed(() => route.params?.dailyId?.toString()),
      enableQuery: {
        loadRecords: computed(() => !!route.params?.dailyId),
      },
    })

    const moduleQuery = useModuleQuery({
      modelId: computed(() => route.params?.id?.toString()),
      enableQuery: {
        loadRecords: false,
        customersCatalog: false,
        loadRecord: false,
        loadHeaders: false,
        loadBundlesFabricated: false,
        loadUsedCoils: false,
      },
    })

    const { orderByOptions } = useOrderByFilter({
      columns: ['created_at'],
    })

    const onAdd = async (payload: TDailyReport) => {
      const dailyReport = await dailyQuery.addRecord.mutateAsync({
        ...payload,
        date: zonedTimeToUtc(new Date(payload.date ?? ''), {
          removeTimeZone: true,
        }),
      })
      onRedirectToLogProduction(dailyReport)
    }

    const onEdit = async (payload: TDailyReport) => {
      await dailyQuery.editRecord.mutateAsync({
        ...payload,
        date: zonedTimeToUtc(new Date(payload.date ?? ''), {
          removeTimeZone: true,
        }),
      })
      moduleQuery.loadHeaders.refetch()
    }

    const onDelete = async (payload: TLog) => {
      await dailyQuery.deleteRecord.mutateAsync(payload)
      moduleQuery.loadHeaders.refetch()
    }

    const onAddLog = async (payload: TLog) => {
      await logReportQuery.addRecord.mutateAsync({
        ...payload,
        log_report_id: Number(route.params?.dailyId?.toString()),
      })
      dailyQuery.loadRecord.refetch()
    }

    const onEditLog = async (payload: TLog) => {
      await logReportQuery.editRecord.mutateAsync(payload)
      dailyQuery.loadRecord.refetch()
    }

    const onDeleteLog = async (payload: TLog) => {
      await logReportQuery.deleteRecord.mutateAsync(payload)
      dailyQuery.loadRecord.refetch()
    }

    const onAction = async (action: TRecordAction) => {
      await dailyQuery.actionRecord.mutateAsync(action)
      dailyQuery.loadRecord.refetch()
    }

    const model = computed(() => dailyQuery.loadRecord?.data?.value)
    const coils = computed(() => dailyQuery.loadCoilStocks?.data?.value)
    const bundles = computed(() => dailyQuery.loadBundlesProject?.data?.value)
    const tableData = computed(() => dailyQuery.loadRecords?.data?.value?.data)
    const tableLogs = computed(() => logReportQuery.loadRecords?.data?.value)

    const isLoading = computed(() => dailyQuery.addRecord?.isLoading.value)

    const isLogsLoading = computed(
      () =>
        logReportQuery.loadRecords?.isLoading.value ||
        logReportQuery.addRecord.isLoading.value ||
        logReportQuery.editRecord.isLoading.value ||
        logReportQuery.deleteRecord.isLoading.value,
    )

    const isCoilStocksLoading = computed(
      () => dailyQuery.loadCoilStocks?.isLoading.value,
    )

    const isBundlesLoading = computed(
      () => dailyQuery.loadBundlesProject?.isLoading.value,
    )

    const onRedirectToLogProduction = (dailyReport: TDailyReport) => {
      router.push({
        name: 'DailyReport',
        params: {
          id: Number(dailyReport.project_id),
          dailyId: Number(dailyReport.id),
        },
      })
    }

    return {
      model,
      coilStocksFilters,
      orderByOptions,
      rows,
      form,
      isLoading,
      coils,
      isCoilStocksLoading,
      logForm,
      bundles,
      isBundlesLoading,
      tableLogs,
      isLogsLoading,
      tableData,
      onEdit,
      onEditLog,
      onAdd,
      onAddLog,
      onDeleteLog,
      onDelete,
      onAction,
    }
  })

export { useProvideDailyReportModule, useDailyReportModule }
